import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Handshake, Trash2, X } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { AreaSensivel, BuscaRemota, CampoData, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { AvisoProfissionalDesativado, CamposReuniaoLivre, estadoLivreNovo, inicioDoEstado, invalidarFichasDoParceiro, mudarHorarioLivre, reuniaoLivreParaApi, usePreparoLivre, validarReuniaoLivre, type EstadoReuniaoLivre } from '../../componentes/reuniao-livre';
import { Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { del, ErroRequisicao, get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { FormOportunidade } from './FormOportunidade';
import { TIPOS_ATIVIDADE, type Atividade, type Oportunidade, type TipoAtividade } from './tipos';
import { CLASSE_AREA, DialogoConcluir } from './ConcluirAtividades';
import { DialogoJustificativa } from './DialogoJustificativa';
import { paraLocal } from './crm-sugestoes';
import { ProximaTarefa, useWorkflows } from './ProximaTarefa';
import { IconeTipoAtividade, juntarDataHora, separarDataHora } from './tarefas-comum';
import { BlocoAgendaReuniao } from './ReuniaoNaTarefa';
import { CHAVE as CHAVE_AGENDA } from '../../componentes/agendamento-comum';

/**
 * CRM no novo desenho, lote 2: criar e editar tarefa (atividade da negociação), como o "Criar Tarefa" do clone do RD. Toda
 * tarefa fica numa negociação; sem ela, salvar oferece criar a negociação agora (complemento 1): o que foi digitado fica
 * guardado (`Rascunho`) e volta preenchido, já ligado à negociação criada. Vários responsáveis (o primeiro marcado é o
 * principal). "Criar próxima tarefa" é este formulário com o painel das sugestões (`painel`).
 *
 * Lista B de 30/10: Responsáveis mostra só os marcados e deixa acrescentar mais pela lista de usuários do workspace (sem
 * "Convidar usuário": o Comercial não acessa essa página, item 11); cancelar e excluir pedem a justificativa (item 13);
 * tarefa cancelada abre aqui como "Reabrir": ao salvar, volta a pendente com o que foi alterado (item 14).
 * Item 23: tarefa nova do tipo Reunião também agenda na Agenda e no Google (a caixa "Agendar na Agenda e no Google" vem
 * marcada). Ficha 2026-11-03: com os mesmos campos livres da Agenda, sem serviço nem recurso; o título e a descrição da reunião
 * são os da tarefa, e a data e a hora dela são o início. Editar e reabrir não mexem no agendamento.
 */

export const CAMINHO_WORKFLOWS = '/crm/configuracoes/workflows';

export interface Rascunho {
  parceiroId: string | null;
  parceiroNome: string | null;
  /** Rodada rrl-3 (SPEC-M1): aberta na ficha de um contato com empresa, o vínculo da reunião sem negociação; a escolha de outro parceiro o zera. */
  contatoDaReuniaoId?: string | null;
  oportunidadeId: string | null;
  oportunidadeTitulo: string | null;
  titulo: string;
  descricao: string;
  tipo: TipoAtividade;
  dataHora: string;
  /** A data é a padrão, ninguém escolheu (`rascunhoInicial`). */
  dataPadrao?: boolean;
  responsaveis: string[];
  concluir: boolean;
  /** Item 23 (só tarefa nova do tipo Reunião): agenda na Agenda e no Google; padrão marcada. */
  agendar?: boolean;
}

/** Teto da descrição da reunião: na tarefa só registrada, o teto é maior (`MAX_DESCRICAO_TAREFA`). */
const MAX_DESCRICAO_REUNIAO = 2000;
const MAX_DESCRICAO_TAREFA = 5000;

/** Campos livres a partir da data e hora da tarefa ("AAAA-MM-DDTHH:MM"). */
function livreDaTarefa(dataHora: string): EstadoReuniaoLivre {
  const d = new Date(dataHora);
  return estadoLivreNovo(Number.isNaN(d.getTime()) ? null : d);
}

/** `reuniao_livre` da tarefa: sem `inicio`, que vem da atividade (`data_hora`). */
function corpoLivre(e: EstadoReuniaoLivre) {
  const { inicio: _inicio, ...corpo } = reuniaoLivreParaApi(e) as ReturnType<typeof reuniaoLivreParaApi> & { inicio?: string };
  return corpo;
}

const umaHoraDepois = () => paraLocal(new Date(Date.now() + 3_600_000).toISOString());

export function rascunhoInicial(usuarioId: string, d: Partial<Rascunho> = {}): Rascunho {
  return { dataPadrao: d.dataHora === undefined, parceiroId: null, parceiroNome: null, oportunidadeId: null, oportunidadeTitulo: null, titulo: '', descricao: '', tipo: 'tarefa', dataHora: umaHoraDepois(), responsaveis: [usuarioId], concluir: false, ...d };
}

export function rascunhoDaAtividade(a: Atividade): Rascunho {
  return {
    parceiroId: a.parceiro_id,
    parceiroNome: a.parceiro_nome,
    oportunidadeId: a.oportunidade_id,
    oportunidadeTitulo: a.oportunidade_titulo,
    titulo: a.titulo,
    descricao: a.descricao ?? '',
    tipo: a.tipo,
    dataHora: paraLocal(a.data_hora),
    responsaveis: (a.responsaveis ?? []).map((r) => r.id),
    concluir: false,
  };
}

export function FormTarefa({
  atual,
  inicial,
  travarNegociacao,
  titulo,
  painel,
  ajuste,
  aoFechar,
  aoSalvo,
  aoPedirConcluir,
}: {
  atual?: Atividade;
  inicial: Rascunho;
  /** A negociação vem dada (ficha da negociação, próxima tarefa): o campo fica travado. */
  travarNegociacao?: boolean;
  titulo?: string;
  /** "Criar próxima tarefa": o painel com o resumo das anteriores e as sugestões, acima do formulário. */
  painel?: ReactNode;
  /** Muda os campos por fora (a sugestão escolhida); `n` muda a cada aplicação. */
  ajuste?: { n: number; dados: Partial<Rascunho> };
  aoFechar: () => void;
  aoSalvo?: (r: { id: string; concluida: boolean }) => void;
  /** Editando uma pendente: o botão "Concluir" fecha o formulário e pede a ata (quem abriu cuida da janela). */
  aoPedirConcluir?: (a: Atividade) => void;
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const { t: ta } = useTranslation('agenda');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const [v, setV] = useState<Rascunho>(inicial);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);
  const [semNegociacao, setSemNegociacao] = useState(false);
  const [criandoNegociacao, setCriandoNegociacao] = useState(false);
  const [cancelar, setCancelar] = useState(false);
  const [excluir, setExcluir] = useState(false);
  const [concluirNovo, setConcluirNovo] = useState<Atividade | null>(null);
  // Rodada crm1 (SPEC-B2): "Marcar como concluída ao criar" também abre o "Criar próxima tarefa", como a Ficha e as Atividades.
  const workflows = useWorkflows();
  const [proxima, setProxima] = useState<{ a: Atividade; ata: string } | null>(null);
  const proximaRef = useRef<{ a: Atividade; ata: string } | null>(null);
  const membros = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros'), staleTime: 300_000 });
  const mudar = (d: Partial<Rascunho>) => {
    setV((x) => ({ ...x, ...d }));
    setErros((e) => {
      const n = { ...e };
      for (const k of Object.keys(d)) delete n[k];
      return n;
    });
  };
  useEffect(() => {
    if (ajuste) setV((x) => ({ ...x, ...ajuste.dados }));
  }, [ajuste?.n]); // eslint-disable-line react-hooks/exhaustive-deps -- só quando a sugestão muda
  // Item 14: tarefa cancelada abre para reabrir; a reunião cancelada perde o vínculo com o agendamento (o horário volta a ser livre).
  const reabrindo = atual?.estado === 'cancelada';
  // Item 23: o agendamento nasce só com a tarefa nova, de quem pode criar na Agenda (o servidor exige a mesma permissão).
  const podeAgendar = !atual && v.tipo === 'reuniao' && s.instalado('agenda') && s.pode('agenda.criar');
  const agendando = podeAgendar && v.agendar !== false;
  const preparo = usePreparoLivre(podeAgendar);
  // Rodada rrl-6 (RRL6-VIS-B3): sem data escolhida, a reunião abre na próxima meia hora, como nos outros pontos de entrada; o "agora mais uma hora" é da tarefa comum.
  const [livre, setLivre] = useState<EstadoReuniaoLivre>(() => (inicial.dataPadrao ? estadoLivreNovo() : livreDaTarefa(inicial.dataHora)));
  const [errosLivre, setErrosLivre] = useState<Record<string, string>>({});
  // Rodada rrl-1 (VIS-M1): o erro só dos campos da reunião também leva o foco. Memo: a validação foca a cada objeto novo.
  const todosOsErros = useMemo(() => ({ ...erros, ...errosLivre }), [erros, errosLivre]);
  const { ref: refForm } = useValidacaoLocal(todosOsErros);
  // Rodada rrl-1 (SPEC-M2): reunião sem negociação é só reunião livre, sem atividade. Rodadas rrl-1 e rrl-2 (ROB-M3, ROB2-M3): um id
  // por abertura do formulário, o mesmo entre tentativas: resposta perdida e salvar de novo não criam outra tarefa nem outra reunião.
  const [idDaCriacao] = useState(() => crypto.randomUUID());
  // A data e a hora da tarefa são o início da reunião: mudar por fora (a sugestão escolhida, o tipo trocado) move os campos.
  const dataHoraVista = useRef(v.dataHora);
  useEffect(() => {
    if (dataHoraVista.current === v.dataHora) return;
    dataHoraVista.current = v.dataHora;
    const { data, hora } = separarDataHora(v.dataHora);
    if (data && hora && (data !== livre.dia || hora !== livre.horaInicio)) setLivre((x) => mudarHorarioLivre(x, { dia: data, horaInicio: hora }));
  }, [v.dataHora]); // eslint-disable-line react-hooks/exhaustive-deps -- só quando a data da tarefa muda
  const mudarLivre = (novo: EstadoReuniaoLivre) => {
    setLivre(novo);
    setErrosLivre({});
    if (inicioDoEstado(novo)) mudar({ dataHora: juntarDataHora(novo.dia, novo.horaInicio) });
  };
  const desativado = agendando && preparo.data?.profissional_desativado === true;
  const agendada = !!atual?.agendamento_id && !reabrindo;

  const recarregar = async (oportunidadeId: string | null) => {
    await qc.invalidateQueries({ queryKey: ['/pipeline/atividades'] });
    await qc.invalidateQueries({ queryKey: ['pipeline'] });
    if (oportunidadeId) await qc.invalidateQueries({ queryKey: ['chatter', 'pipeline.oportunidade', oportunidadeId] });
  };

  const salvar = async () => {
    const e: Record<string, string> = {};
    if (!v.titulo.trim()) e.titulo = t('crm.form.obrigatorio');
    if (!v.dataHora) e.dataHora = t('crm.form.obrigatorio');
    // A reunião pede só o horário: o título é o da tarefa, e a descrição dela tem o teto da reunião.
    const noLivre = agendando ? validarReuniaoLivre(livre, t('crm.form.obrigatorio'), ta) : {};
    setErrosLivre(noLivre);
    if (agendando) {
      delete e.dataHora;
      if (v.descricao.length > MAX_DESCRICAO_REUNIAO) e.descricao = t('crm.form.descricaoLonga', { max: MAX_DESCRICAO_REUNIAO });
    }
    const soReuniao = agendando && !v.oportunidadeId;
    if (!v.responsaveis.length && !soReuniao) e.responsaveis = t('crm.form.semResponsavel');
    if (!v.oportunidadeId && !agendando) {
      // Complemento 1: sem negociação, oferece criar agora (o resto do formulário fica guardado).
      setErros({ ...e, oportunidadeId: t('crm.form.selecioneNegociacao') });
      setSemNegociacao(true);
      return;
    }
    setErros(e);
    if (Object.keys(e).length || Object.keys(noLivre).length) return;
    setSalvando(true);
    try {
      if (soReuniao) {
        const vinculo = v.contatoDaReuniaoId ?? v.parceiroId;
        await post('/agenda/reunioes', { id: idDaCriacao, assunto: v.titulo.trim(), observacoes: v.descricao.trim() || null, ...reuniaoLivreParaApi(livre), ...(vinculo ? { parceiro_id: vinculo } : {}) });
        await qc.invalidateQueries({ queryKey: [CHAVE_AGENDA] });
        await qc.invalidateQueries({ queryKey: ['/agenda/agendamentos'] });
        if (vinculo) await invalidarFichasDoParceiro(qc, vinculo);
        toast.sucesso(ta(livre.meet ? 'reuniaoLivre.salvaComMeet' : 'crm.agendada'));
        aoFechar();
        return;
      }
      const [principal, ...outros] = v.responsaveis;
      // Dia inteiro: a atividade fica na meia-noite do primeiro dia; o dia da reunião vai como data, no `reuniao_livre`.
      const inicioReuniao = agendando ? (livre.diaInteiro ? new Date(`${livre.dia}T00:00`) : inicioDoEstado(livre)) : null;
      const corpo = { titulo: v.titulo.trim(), descricao: v.descricao.trim() || null, tipo: v.tipo, data_hora: (inicioReuniao ?? new Date(v.dataHora)).toISOString(), responsavel_id: principal ?? null, outros_responsaveis: outros };
      if (atual) {
        if (reabrindo) await post(`/pipeline/atividades/${atual.id}/reabrir`, corpo);
        else await patch(`/pipeline/atividades/${atual.id}`, agendada ? { ...corpo, data_hora: undefined } : corpo);
        await recarregar(atual.oportunidade_id);
        toast.sucesso(t(reabrindo ? 'crm.form.reaberta' : 'crm.form.atualizada'));
        aoSalvo?.({ id: atual.id, concluida: false });
        aoFechar();
      } else {
        const r = await post<{ id: string }>('/pipeline/atividades', {
          id: idDaCriacao,
          ...corpo,
          oportunidade_id: v.oportunidadeId,
          ...(agendando ? { reuniao_livre: corpoLivre(livre) } : {}),
        });
        if (agendando) {
          await qc.invalidateQueries({ queryKey: [CHAVE_AGENDA] });
          await qc.invalidateQueries({ queryKey: ['/agenda/agendamentos'] });
          if (v.parceiroId) await invalidarFichasDoParceiro(qc, v.parceiroId);
        }
        await recarregar(v.oportunidadeId);
        toast.sucesso(t(agendando && livre.meet ? 'crm.form.criadaComMeet' : 'crm.form.criada'));
        if (v.concluir) {
          // "Marcar como concluída ao criar" também pede a ata.
          const lida = await get<{ itens: Atividade[] }>('/pipeline/atividades', { oportunidade_id: v.oportunidadeId, filtro: 'pendentes', limite: 100 });
          const nova = lida.itens.find((x) => x.id === r.id);
          if (nova) return setConcluirNovo(nova);
        }
        aoSalvo?.({ id: r.id, concluida: false });
        aoFechar();
      }
    } catch (err) {
      // Erro de horário, convidado ou Meet volta no campo da reunião (a reunião nunca recusa por conflito).
      if (agendando && err instanceof ErroRequisicao) setErrosLivre(err.porCampo());
      toast.erro(mensagemDeErro(err));
    } finally {
      setSalvando(false);
    }
  };

  const cancelarTarefa = async (justificativa: string) => {
    if (!atual) return;
    try {
      await post(`/pipeline/atividades/${atual.id}/cancelar`, { justificativa });
      await recarregar(atual.oportunidade_id);
      toast.sucesso(t('crm.form.cancelada'));
      aoFechar();
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    }
  };

  // Ajustes do CRM 27/10 (decisão 12, F2): "Excluir" de verdade, ao lado do "Cancelar tarefa".
  const excluirTarefa = async (justificativa: string) => {
    if (!atual) return;
    try {
      await del(`/pipeline/atividades/${atual.id}`, { justificativa });
      await recarregar(atual.oportunidade_id);
      toast.sucesso(t('crm.form.excluida'));
      aoFechar();
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    }
  };

  const tituloGaveta = titulo ?? (reabrindo ? t('crm.form.reabrirTitulo') : atual ? t('crm.form.editarTitulo') : t('crm.form.criarTitulo'));
  const formId = `${id}-form`;
  return (
    <>
      <Drawer
        aberto={!criandoNegociacao && !concluirNovo && !proxima}
        aoFechar={aoFechar}
        titulo={tituloGaveta}
        largura="lg"
        rodape={
          <>
            {atual && s.pode('pipeline.excluir') && (!atual.agendamento_id || s.pode('agenda.cancelar')) && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" aria-hidden />} onClick={() => setExcluir(true)} data-excluir-tarefa="">
                {t('crm.form.excluir')}
              </Botao>
            )}
            {atual && atual.estado === 'pendente' && s.pode('pipeline.editar') && (!atual.agendamento_id || s.pode('agenda.cancelar')) && (
              <Botao variante="fantasma" onClick={() => setCancelar(true)}>
                {t('crm.form.cancelarTarefa')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.cancelar')}
            </Botao>
            {/* Review rlb-1 (VIS-M2): só a pendente conclui (a cancelada, no "Reabrir tarefa", abria "Concluir 0 tarefas"). */}
            {atual && atual.estado === 'pendente' && aoPedirConcluir && s.pode('pipeline.editar') && (
              <Botao
                data-concluir-do-form=""
                onClick={() => {
                  aoFechar();
                  aoPedirConcluir(atual);
                }}
              >
                {t('acoes.concluir')}
              </Botao>
            )}
            <Botao variante="primario" type="submit" form={formId} carregando={salvando} disabled={desativado} aria-describedby={desativado ? `${id}-desativado` : undefined}>
              {reabrindo ? t('crm.form.reabrir') : tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <form
          ref={refForm}
          id={formId}
          noValidate
          data-form-tarefa=""
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void salvar();
          }}
        >
          {reabrindo && (
            <p className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2" data-reabrir-dica="">
              {t('crm.form.reabrirDica')}
            </p>
          )}
          {painel ?? (
            !atual && (
              <p className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2">
                {t('crm.form.dica')}{' '}
                <Link to={CAMINHO_WORKFLOWS} className="font-medium text-acento-forte underline" onClick={aoFechar}>
                  {t('crm.form.conhecerWorkflows')}
                </Link>
              </p>
            )
          )}
          <Rotulo id={`${id}-empresa`} rotulo={t('crm.form.empresa')}>
            <BuscaRemota
              id={`${id}-empresa`}
              def={{ caminho: '/cadastros/parceiros', rotulo: (i) => String(i.nome) }}
              valor={v.parceiroId}
              rotuloAtual={v.parceiroNome ?? undefined}
              placeholder={t('crm.form.selecionar')}
              aoMudar={(x, item) => (travarNegociacao || atual ? undefined : mudar({ parceiroId: x, parceiroNome: item ? String(item.nome) : null, contatoDaReuniaoId: null, oportunidadeId: null, oportunidadeTitulo: null }))}
            />
          </Rotulo>
          <div className="flex flex-col gap-1.5">
            <Rotulo id={`${id}-negociacao`} rotulo={t('crm.form.negociacao')} obrigatorio={!agendando} erro={erros.oportunidadeId}>
              {travarNegociacao || atual ? (
                <EntradaSensivel mascara={null} livre nomesNoTexto={[v.parceiroNome]} id={`${id}-negociacao`} value={v.oportunidadeTitulo ?? ''} disabled readOnly />
              ) : (
                <BuscaRemota
                  id={`${id}-negociacao`}
                  key={v.parceiroId ?? 'todas'}
                  def={{ caminho: '/pipeline/oportunidades', params: { estado: 'aberta', ...(v.parceiroId ? { parceiro_id: v.parceiroId } : {}) }, rotulo: (i) => String(i.titulo) }}
                  valor={v.oportunidadeId}
                  rotuloAtual={v.oportunidadeTitulo ?? undefined}
                  placeholder={t('crm.form.selecionar')}
                  aria-invalid={!!erros.oportunidadeId || undefined}
                  aoMudar={(x, item) => mudar({ oportunidadeId: x, oportunidadeTitulo: item ? String(item.titulo) : null, ...(item && !v.parceiroId ? { parceiroId: String(item.parceiro_id), parceiroNome: (item.parceiro_nome as string | null) ?? null } : {}) })}
                />
              )}
            </Rotulo>
            {!travarNegociacao && !atual && s.pode('pipeline.criar') && (
              <button type="button" className="flex w-fit items-center gap-1 text-sm font-medium text-acento-forte hover:underline" onClick={() => setCriandoNegociacao(true)} data-criar-negociacao-tarefa="">
                <Handshake className="size-4" aria-hidden />
                {t('crm.form.criarNegociacao')}
              </button>
            )}
          </div>
          <Rotulo id={`${id}-titulo`} rotulo={t('crm.form.assunto')} obrigatorio erro={erros.titulo}>
            <EntradaSensivel mascara={null} livre nomesNoTexto={[v.parceiroNome]} id={`${id}-titulo`} name="titulo" value={v.titulo} maxLength={200} aria-invalid={!!erros.titulo || undefined} onChange={(e) => mudar({ titulo: e.target.value })} placeholder={t('crm.form.assunto')} />
          </Rotulo>
          <Rotulo id={`${id}-descricao`} rotulo={t('crm.form.descricao')} erro={erros.descricao} ajuda={agendando ? t('crm.form.descricaoConvidados') : undefined}>
            <AreaSensivel nomesNoTexto={[v.parceiroNome]} rotulo={t('crm.form.descricao')} id={`${id}-descricao`} aria-describedby={erros.descricao ? `${id}-descricao-erro` : agendando ? `${id}-descricao-ajuda` : undefined} name="descricao" rows={3} maxLength={agendando ? MAX_DESCRICAO_REUNIAO : MAX_DESCRICAO_TAREFA} aria-invalid={!!erros.descricao || undefined} className={CLASSE_AREA} value={v.descricao} onChange={(e) => mudar({ descricao: e.target.value })} placeholder={t('crm.form.descricao')} />
          </Rotulo>
          <fieldset aria-describedby={erros.responsaveis ? `${id}-resp-erro` : undefined}>
            <legend className="mb-1.5 text-sm font-medium text-tinta-2">
              {t('crm.form.responsaveis')}
              {(!agendando || !!v.oportunidadeId) && <span className="ml-0.5 text-acento-forte">*</span>}
            </legend>
            {/* Item 11: só os marcados; mais pessoas entram pela lista de usuários do workspace (sem convite aqui). */}
            <ul className="flex flex-wrap gap-2" data-responsaveis="">
              {v.responsaveis.map((uid, i) => {
                const nome = nomePessoa((membros.data?.itens ?? []).find((m) => m.id === uid)?.nome ?? atual?.responsaveis?.find((r) => r.id === uid)?.nome ?? '');
                return (
                  <li key={uid} className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-borda bg-sup-1 pl-3 pr-1 text-sm" data-responsavel={uid}>
                    {nome}
                    {i === 0 && v.responsaveis.length > 1 && <span className="rounded bg-sup-3 px-1.5 text-xs text-tinta-2">{t('crm.form.principal')}</span>}
                    <button
                      type="button"
                      aria-label={t('crm.form.removerResponsavel', { nome })}
                      title={t('crm.form.removerResponsavel', { nome })}
                      className="grid size-6 place-items-center rounded-full text-tinta-3 hover:bg-sup-3 hover:text-tinta-1"
                      onClick={() => mudar({ responsaveis: v.responsaveis.filter((x) => x !== uid) })}
                      data-remover-responsavel={uid}
                    >
                      <X className="size-3.5" aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
            {(() => {
              const disponiveis = (membros.data?.itens ?? []).filter((m) => !v.responsaveis.includes(m.id));
              return (
                disponiveis.length > 0 && (
                  <div className="mt-2 sm:max-w-xs" data-adicionar-responsavel="">
                    <Selecao
                      aria-label={t('crm.form.adicionarResponsavel')}
                      value=""
                      vazio={t('crm.form.adicionarResponsavel')}
                      opcoes={disponiveis.map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))}
                      onChange={(e) => e.target.value && mudar({ responsaveis: [...v.responsaveis, e.target.value] })}
                    />
                  </div>
                )
              );
            })()}
            {erros.responsaveis && (
              <p id={`${id}-resp-erro`} className="mt-1 text-xs text-perigo">
                {erros.responsaveis}
              </p>
            )}
          </fieldset>
          <Rotulo id={`${id}-tipo`} rotulo={t('crm.form.tipo')} obrigatorio>
            <div className="flex items-center gap-2">
              <span className="grid size-10 shrink-0 place-items-center rounded-lg border border-borda bg-sup-2" data-icone-tipo={v.tipo}>
                <IconeTipoAtividade tipo={v.tipo} />
              </span>
              <Selecao id={`${id}-tipo`} name="tipo" value={v.tipo} opcoes={TIPOS_ATIVIDADE.map((x) => ({ valor: x, rotulo: t(`tipoAtividade.${x}`) }))} onChange={(e) => mudar({ tipo: e.target.value as TipoAtividade })} />
            </div>
          </Rotulo>
          {podeAgendar && <BlocoAgendaReuniao id={id} agendar={agendando} aoMudar={(x) => mudar({ agendar: x, ...(x ? { concluir: false } : {}) })} />}
          {desativado && <AvisoProfissionalDesativado id={`${id}-desativado`} />}
          {agendando ? (
            <CamposReuniaoLivre idBase={`${id}-reuniao`} estado={livre} aoMudar={mudarLivre} erros={errosLivre} preparo={preparo} nomes={[v.parceiroNome]} />
          ) : (
            /* C18: data do agendamento e horário em dois campos, como a origem. */
            <div className="grid gap-4 sm:grid-cols-2">
              <Rotulo id={`${id}-data`} rotulo={t('crm.form.data')} obrigatorio erro={erros.dataHora} ajuda={agendada ? t('crm.form.reuniaoAgenda') : undefined}>
                <CampoData id={`${id}-data`} name="data" type="date" value={separarDataHora(v.dataHora).data} disabled={agendada} onChange={(e) => mudar({ dataHora: juntarDataHora(e.target.value, separarDataHora(v.dataHora).hora) })} />
              </Rotulo>
              <Rotulo id={`${id}-hora`} rotulo={t('crm.form.horario')} obrigatorio>
                <CampoData id={`${id}-hora`} name="hora" type="time" value={separarDataHora(v.dataHora).hora} disabled={agendada} onChange={(e) => mudar({ dataHora: juntarDataHora(separarDataHora(v.dataHora).data, e.target.value) })} />
              </Rotulo>
            </div>
          )}
          {!atual && !agendando && (
            <label className="flex min-h-8 items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={v.concluir} onChange={(e) => mudar({ concluir: e.target.checked })} data-concluir-ao-criar="" />
              {t('crm.form.concluirAoCriar')}
            </label>
          )}
        </form>
      </Drawer>
      <Modal
        aberto={semNegociacao}
        aoFechar={() => setSemNegociacao(false)}
        titulo={t('crm.semNegociacao.titulo')}
        rodape={
          <>
            <Botao
              variante="fantasma"
              onClick={() => {
                setSemNegociacao(false);
                requestAnimationFrame(() => document.getElementById(`${id}-negociacao`)?.focus());
              }}
            >
              {t('crm.semNegociacao.escolher')}
            </Botao>
            {s.pode('pipeline.criar') && (
              <Botao
                variante="primario"
                onClick={() => {
                  setSemNegociacao(false);
                  setCriandoNegociacao(true);
                }}
              >
                {t('crm.semNegociacao.criar')}
              </Botao>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-2 text-sm" data-sem-negociacao="">
          <p>{t('crm.semNegociacao.texto')}</p>
          <p className="text-tinta-3">{t('crm.semNegociacao.guardado')}</p>
        </div>
      </Modal>
      {criandoNegociacao && (
        <NegociacaoParaTarefa
          parceiroId={v.parceiroId}
          parceiroNome={v.parceiroNome}
          aoVoltar={(op) => {
            setCriandoNegociacao(false);
            if (op) mudar({ oportunidadeId: op.id, oportunidadeTitulo: op.titulo, parceiroId: op.parceiro_id, parceiroNome: op.parceiro_nome, ...(op.parceiro_id !== v.parceiroId ? { contatoDaReuniaoId: null } : {}) });
          }}
        />
      )}
      {concluirNovo && (
        <DialogoConcluir
          atividades={[concluirNovo]}
          aoConcluir={({ ata }) => {
            proximaRef.current = (workflows.data?.abrir_proxima ?? true) ? { a: concluirNovo, ata } : null;
            setProxima(proximaRef.current);
          }}
          aoFechar={() => {
            const n = concluirNovo;
            setConcluirNovo(null);
            if (proximaRef.current) return;
            aoSalvo?.({ id: n.id, concluida: false });
            aoFechar();
          }}
        />
      )}
      {proxima && (
        <ProximaTarefa
          concluida={proxima.a}
          ata={proxima.ata}
          aoFechar={() => {
            const n = proxima.a;
            proximaRef.current = null;
            setProxima(null);
            aoSalvo?.({ id: n.id, concluida: false });
            aoFechar();
          }}
        />
      )}
      <DialogoJustificativa
        aberto={excluir}
        aoFechar={() => setExcluir(false)}
        aoConfirmar={(j) => {
          setExcluir(false);
          void excluirTarefa(j);
        }}
        titulo={t('crm.form.confirmarExcluir')}
        texto={t('crm.form.confirmarExcluirTexto')}
        botao={t('crm.form.excluir')}
      />
      <DialogoJustificativa
        aberto={cancelar}
        aoFechar={() => setCancelar(false)}
        aoConfirmar={(j) => {
          setCancelar(false);
          void cancelarTarefa(j);
        }}
        titulo={t('crm.form.confirmarCancelar')}
        texto={t('crm.form.confirmarCancelarTexto')}
        botao={t('crm.form.cancelarTarefa')}
      />
    </>
  );
}

/**
 * Complemento 1: "Criar negociação para a tarefa". O formulário de negociação de sempre, com o aviso de que a tarefa ficou
 * guardada; "Criar e voltar para a tarefa" devolve a negociação criada (fechar volta sem ela).
 */
function NegociacaoParaTarefa({ parceiroId, parceiroNome, aoVoltar }: { parceiroId: string | null; parceiroNome: string | null; aoVoltar: (op: Oportunidade | null) => void }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const qc = useQueryClient();
  return (
    <Drawer
      aberto
      aoFechar={() => aoVoltar(null)}
      titulo={t('crm.negociacaoTarefa.titulo')}
      largura="lg"
      rodape={
        <>
          <Botao variante="fantasma" onClick={() => aoVoltar(null)}>
            {t('crm.negociacaoTarefa.voltar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-oportunidade" data-criar-voltar="">
            {t('crm.negociacaoTarefa.criarVoltar')}
          </Botao>
        </>
      }
    >
      <p className="mb-4 rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2" data-aviso-tarefa-guardada="">
        {t('crm.negociacaoTarefa.aviso')}
      </p>
      <FormOportunidade
        usuarioId={s.usuario.id}
        parceiroInicial={parceiroId ? { id: parceiroId, nome: parceiroNome ?? '' } : undefined}
        aoSalvar={async (r) => {
          if (!r.id) return;
          await qc.invalidateQueries({ queryKey: ['pipeline'] });
          // rl0211-12: a negociação criada aqui aparece na ficha da empresa ou do contato de onde a tarefa saiu (lista e verticais).
          await qc.invalidateQueries({ queryKey: ['crm'] });
          aoVoltar(await get<Oportunidade>(`/pipeline/oportunidades/${r.id}`));
        }}
      />
    </Drawer>
  );
}
